package io.horion.cv.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.styles.ValidationPopupContainerStyle
import io.horion.cv.styles.ValidationPopupHeaderGapStyle
import io.horion.cv.styles.ValidationPopupIconBoxStyle
import io.horion.cv.styles.ValidationPopupIconStyle
import io.horion.cv.styles.ValidationPopupTitleTextStyle
import io.horion.cv.utils.Colors
import io.horion.cv.utils.Constants.INTER
import kotlinx.browser.document
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventListener

@Composable
fun ValidationWarningPopup(
    missingLabels: List<String>,
    onDismiss: () -> Unit,
    onFieldClick: ((Int) -> Unit)? = null,
    openDownward: Boolean = false,
) {
    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    val redText = if (isDark) Colors.WarningTextDark else Colors.WarningText
    val redSoftBg = if (isDark) Colors.WarningSoftBgDark else Colors.WarningSoftBg
    val redDot = if (isDark) Colors.WarningDotDark else Colors.WarningDot
    val panelBg = if (isDark) Colors.PopupBgDark else Colors.White
    val subtitleCol = if (isDark) Colors.SubtitleGrayDark else Colors.SubtitleGray
    val closeCol = if (isDark) Colors.CloseGrayDark else Colors.CloseGray
    val shadowCol = if (isDark) "rgba(0,0,0,0.5)" else "rgba(0,0,0,0.18)"
    val panelBgStr = panelBg.toString()

    DisposableEffect(Unit) {
        val outsideClickListener = EventListener { event: Event ->
            val panel = document.getElementById("validation-warning-panel")
            val target = event.target
            if (panel != null && target != null && !(panel.asDynamic().contains(target) as Boolean)) {
                onDismiss()
            }
        }
        val scrollListener = EventListener { _: Event -> onDismiss() }

        document.addEventListener("mousedown", outsideClickListener, true)
        document.addEventListener("scroll", scrollListener, true)

        onDispose {
            document.removeEventListener("mousedown", outsideClickListener, true)
            document.removeEventListener("scroll", scrollListener, true)
        }
    }

    Box(
        modifier = ValidationPopupContainerStyle.toModifier()
            .position(Position.Absolute)
            .id("validation-warning-panel")
            .styleModifier {
                if (openDownward) property("top", "calc(100% + 12px)")
                else property("bottom", "calc(100% + 12px)")
                property("right", "0")
                property("z-index", "50")
                property("animation", "popUp 0.28s cubic-bezier(0.34,1.56,0.64,1)")
                property("box-shadow", "0 12px 34px $shadowCol")
                property("box-sizing", "border-box")
                property("transition", "background-color 250ms ease")
            }
            .backgroundColor(panelBg)
            .borderRadius(14.px)
    ) {
        Column(verticalArrangement = ValidationPopupHeaderGapStyle.toModifier() as Arrangement.Vertical) {

            // En-tête : icône + titre + croix
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.px)
            ) {
                Box(
                    modifier = ValidationPopupIconBoxStyle.toModifier()
                        .borderRadius(50.percent)
                        .backgroundColor(redSoftBg)
                        .styleModifier { property("flex-shrink", "0") },
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        src = "warning.svg",
                        description = "Avertissement",
                        modifier = ValidationPopupIconStyle.toModifier()
                    )
                }
                SpanText(
                    "Champs obligatoires manquants",
                    modifier = ValidationPopupTitleTextStyle.toModifier()
                        .fontFamily(INTER)
                        .fontWeight(FontWeight.Bold)
                        .color(redText)
                        .styleModifier { property("flex", "1") }
                )
                Box(
                    modifier = Modifier
                        .size(22.px)
                        .borderRadius(50.percent)
                        .cursor(Cursor.Pointer)
                        .onClick { onDismiss() },
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        "✕",
                        modifier = Modifier
                            .fontSize(13.px)
                            .color(closeCol)
                    )
                }
            }

            // Sous-titre explicatif
            SpanText(
                "Complétez ces champs avant de télécharger :",
                modifier = Modifier
                    .fontFamily(INTER)
                    .fontSize(12.px)
                    .color(subtitleCol)
            )

            // Liste des champs manquants
            Div(attrs = Modifier
                .fillMaxWidth()
                .toAttrs {
                    style {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("gap", "8px")
                    }
                }
            ) {
                missingLabels.forEachIndexed { index, label ->
                    Row(
                        modifier = Modifier
                            .backgroundColor(redSoftBg)
                            .borderRadius(20.px)
                            .padding(topBottom = 5.px, leftRight = 12.px)
                            .then(
                                if (onFieldClick != null)
                                    Modifier.cursor(Cursor.Pointer).onClick { onFieldClick(index) }
                                else Modifier
                            ),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(6.px)
                    ) {
                        Box(
                            modifier = Modifier
                                .size(5.px)
                                .borderRadius(50.percent)
                                .backgroundColor(redDot)
                        )
                        SpanText(
                            label,
                            modifier = Modifier
                                .fontFamily(INTER)
                                .fontSize(11.px)
                                .fontWeight(FontWeight.Medium)
                                .color(redText)
                        )
                    }
                }
            }
        }

        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    if (openDownward) property("top", "-6px")
                    else property("bottom", "-6px")
                    property("right", "22px")
                    property("width", "12px")
                    property("height", "12px")
                    property("background", panelBgStr)
                    property("transform", "rotate(45deg)")
                    property(
                        "box-shadow",
                        if (openDownward) "-3px -3px 6px rgba(0,0,0,0.06)"
                        else "3px 3px 6px rgba(0,0,0,0.06)"
                    )
                }
        )
    }
}